<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <BRow
      class="py-2"
    >
      <BCol
        cols="12"
        md="9"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="tableDocumentTypes.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="3"
      >
        <BFormGroup
          label="Role"
          label-for="filterRole"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="tableDocumentTypes.selected.role"
            placeholder="filter role here"
            input-id="filterRole"
            label="name"
            clearable
            append-to-body
            :options="(tableDocumentTypes.options.roles)"
            :loading="(tableDocumentTypes.fetching.roles)"
            :disabled="(tableDocumentTypes.busy || tableDocumentTypes.fetching.roles)"
            :calculate-position="calculateDropPosition"
          >
            <template #option="{ name }">
              <div
                class="text-break text-wrap"
                v-text="name"
              />
            </template>
            <template #selected-option="{ name }">
              <div
                class="w-225px w-md-100px w-lg-200px text-truncate"
                v-text="name"
              />
            </template>
            <template #no-options="">
              no available role
            </template>
          </VSelect>
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
      >
        <BButton
          size="sm"
          variant="primary"
          @click="createDocumentType"
        >
          Create Document Type
        </BButton>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableDocumentTypes"
      :provider="tableProvider"
      :fields="tableDocumentTypes.fields"
      :filter="tableDocumentTypes.filter"
      :filter-on="tableDocumentTypes.filterOn"
      :sort-by="tableDocumentTypes.sortBy"
      :sort-desc="tableDocumentTypes.sortDesc"
      :sort-direction="tableDocumentTypes.sortDirection"
      :total-rows="tableDocumentTypes.totalRows"
      :current-page="tableDocumentTypes.currentPage"
      :per-page="tableDocumentTypes.perPage"
      :stacked="tableDocumentTypes.stacked"
      :busy="tableDocumentTypes.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center">
          <BButton
            size="sm"
            variant="primary"
            :disabled="tableDocumentTypes.busy"
            @click.prevent="editDocumentType(item)"
          >
            Edit
          </BButton>
        </div>
      </template>
    </CustomTable>

    <BModal
      ref="modalDocumentType"
      scrollable
      centered
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      aria-hidden="false"
      role="dialog"
      :title="MCropCode"
    >
      <ValidationObserver
        ref="formDocumentType"
      >
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <BRow>

            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="role_id"
                name="role"
                rules="required"
              >
                <BFormGroup
                  label="Role"
                  label-for="fRole"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <VSelect
                    v-model="selected.role"
                    input-id="fRole"
                    placeholder="select role here"
                    label="name"
                    clearable
                    append-to-body
                    :options="(options.roles)"
                    :loading="(fetching.roles)"
                    :disabled="(state.busy)"
                    :calculate-position="calculateDropPosition"
                    :class="{ 'is-invalid': errors.length > 0 }"
                  >
                    <template #option="{ name }">
                      <div
                        class="text-break text-wrap"
                        v-text="name"
                      />
                    </template>
                    <template #selected-option="{ name }">
                      <div
                        class="w-200px w-md-300px text-truncate"
                        v-text="name"
                      />
                    </template>
                    <template #no-options="">
                      no available roles
                    </template>
                  </VSelect>
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="document_category"
                name="document category"
                rules="required"
              >
                <BFormGroup
                  label="Document Category"
                  label-for="fDocumentCategory"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <VSelect
                    v-model="selected.documentCategory"
                    input-id="fDocumentCategory"
                    placeholder="select document category here"
                    label="name"
                    clearable
                    append-to-body
                    :options="(options.documentCategories)"
                    :loading="(fetching.documentCategories)"
                    :disabled="(state.busy)"
                    :calculate-position="calculateDropPosition"
                    :class="{ 'is-invalid': errors.length > 0 }"
                  >
                    <template #option="{ name }">
                      <div
                        class="text-break text-wrap"
                        v-text="name"
                      />
                    </template>
                    <template #selected-option="{ name }">
                      <div
                        class="w-200px w-md-300px text-truncate"
                        v-text="name"
                      />
                    </template>
                    <template #no-options="">
                      no available document categories
                    </template>
                  </VSelect>
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                name="type code"
                vid="document_type_code"
                rules="max:150"
              >
                <BFormGroup
                  label="Type Code"
                  label-for="fDocumentTypeCode"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fDocumentTypeCode"
                    v-model="formDocumentType.document_type_code"
                    type="text"
                    maxlength="150"
                    autocomplete="off"
                    placeholder="enter type code here"
                    :state="errors.length > 0 ? false : null"
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                name="document type name"
                vid="document_type_name"
                rules="required|max:150"
              >
                <BFormGroup
                  label="Type Name"
                  label-for="fDocumentTypeName"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fDocumentTypeName"
                    v-model="formDocumentType.document_type_name"
                    type="text"
                    maxlength="100"
                    autocomplete="off"
                    placeholder="enter type name here"
                    :state="errors.length > 0 ? false : null"
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                name="upload limit"
                vid="upload_limit"
                rules="max_value:10"
              >
                <BFormGroup
                  label="Upload Limit"
                  label-for="fUploadLimit"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fUploadLimit"
                    v-model="formDocumentType.upload_limit"
                    type="number"
                    max="10"
                    min="1"
                    autocomplete="off"
                    placeholder="enter upload limit here"
                    :state="errors.length > 0 ? false : null"
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              v-if="state.editing"
              cols="12"
            >
              <BFormGroup>
                <BFormCheckbox
                  id="fActive"
                  v-model="formDocumentType.active"
                  switch
                  value="1"
                  name="active"
                  unchecked-value="0"
                  :disabled="state.busy"
                >
                  Is Active?
                </BFormCheckbox>
              </BFormGroup>
            </BCol>
          </BRow>

        </form>
      </ValidationObserver>

      <template #modal-footer="{ cancel }">
        <BButton

          variant="primary"
          :disabled="state.busy"
          @click.prevent="validateDocumentType"
        >
          {{ state.editing ? 'Update' : 'Create' }} Document Type
        </BButton>
        <BButton

          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Discard
        </BButton>
      </template>

    </BModal>
  </BContainer>
</template>

<script>
import QUERY from 'lodash'
import { mapGetters } from 'vuex'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MRoles from '@/mixins/accounts/roles'

import { SAdminDocumentType } from '@/services/admin'

export default {
  name: 'VAdminDocumentTypes',

  mixins: [
    MxMisc,
    MxFormatter,
    MRoles
  ],

  middleware: [
    'auth',
    'admin'
  ],

  metaInfo: () => ({
    title: 'Document Types'
  }),

  data () {
    return {
      state: {
        busy: false,
        editing: false
      },
      fetching: {
        documentCategories: false
      },
      selected: {
        documentCategory: ''
      },
      options: {
        documentCategories: [
          { id: 'Grower', name: 'Grower' },
          { id: 'Event', name: 'Event' },
          { id: 'Others', name: 'Others' }
        ]
      },
      formDocumentType: {
        id: '',
        role_id: '',
        document_type_category: '',
        document_type_code: '',
        document_type_name: '',
        upload_limit: 3,
        active: 1
      },
      tableDocumentTypes: {
        busy: false,
        fetching: {
          roles: false
        },
        options: {
          roles: []
        },
        selected: {
          role: ''
        },
        filter: {
          role: '',
          search: ''
        },
        filterOn: [],
        stacked: 'sm',
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 9, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 8, key: 'created_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true },
          { mobile: 1, key: 'role.role_name', label: 'Role', class: 'min-w-175px' },
          { mobile: 2, key: 'document_type_code', class: 'min-w-150px' },
          { mobile: 3, key: 'document_type_name', class: 'min-w-175px' },
          { mobile: 4, key: 'document_type_category', class: 'min-w-150px' },
          { mobile: 5, key: 'document_type_name', class: 'min-w-175px' },
          { mobile: 6, key: 'upload_limit', class: 'text-md-center w-md-75px' },
          { mobile: 7, key: 'updated_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'auth/user'
    }),

    MCropCode () {
      return this.state.editing ? 'Edit Document Type' : 'Create Document Type'
    }
  },

  watch: {
    'selected.role' (role) {
      this.formDocumentType.role_id = role?.id || ''
    },

    'selected.documentCategory' (documentCategory) {
      this.formDocumentType.document_type_category = documentCategory?.id || ''
    },

    'tableDocumentTypes.selected.role' (role) {
      this.tableDocumentTypes.filter.role = role?.id || ''
    }
  },

  mounted () {
    if (this.currentUser) {
      this.getRoles(this.tableDocumentTypes)
    }
  },

  methods: {

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableDocumentTypes.busy = true

      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        role_id: filter.role
      }

      return await SAdminDocumentType.get(payload).then(
        ({ data }) => {
          this.tableDocumentTypes.totalRows = data.total_rows
          return data.items
        }
      ).catch(() => {
        this.tableDocumentTypes.totalRows = 0
        return []
      }).finally(() => {
        this.tableDocumentTypes.busy = false
      })
    },

    createDocumentType () {
      this.state.editing = false

      this.selected.documentCategory = ''

      this.formDocumentType = Object.assign(this.formDocumentType, {
        id: '',
        role_id: '',
        document_type_code: '',
        document_type_name: '',
        upload_limit: 3,
        active: 1
      })

      this.getRoles(this)

      this.$refs.modalDocumentType.show()
    },

    editDocumentType (documentType) {
      this.state.editing = true

      this.formDocumentType = Object.assign(this.formDocumentType, {
        id: documentType.id,
        role_id: documentType?.role_id || '',
        document_type_code: documentType.document_type_code,
        document_type_name: documentType.document_type_name,
        upload_limit: documentType?.upload_limit || 3,
        active: documentType.active
      })

      this.selected.documentCategory = this.options.documentCategories.find(
        category => String(category.id) === String(documentType.document_type_category)
      ) || ''

      this.getRoles(this).finally(() => {
        this.selected.role = QUERY.find(this.options.roles, {
          id: documentType?.role_id || ''
        }) || ''
      })

      this.$refs.modalDocumentType.show()
    },

    async validateDocumentType () {
      await this.$refs.formDocumentType.validate().then(
        async validDocumentType => {
          if (!validDocumentType) {
            return this.swalInvalid()
          }

          this.swalConfirm({
            html: this.state.editing ? '<h6>Update this Document Type?</h6>' : '<h6>Create this Document Type?</h6>',
            confirmButtonText: this.state.editing ? 'Update' : 'Create',
            preConfirm: async () => {
              this.state.busy = true

              const createOrUpdate = async () => {
                return this.state.editing ? this.putDocumentType() : this.postDocumentType()
              }

              return createOrUpdate().catch(
                ({ message }) => {
                  this.$refs.formDocumentType.setErrors(message.errors)
                }
              ).finally(() => {
                this.state.busy = false
              })
            }
          })
        }
      )
    },

    async postDocumentType () {
      return await SAdminDocumentType.post(this.formDocumentType).then(
        ({ data: { message } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.modalDocumentType.hide()
            this.$refs.tableDocumentTypes.refetch()
          })
        }
      )
    },

    async putDocumentType () {
      return await SAdminDocumentType.put(this.formDocumentType).then(
        ({ data: { message, document_type } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.modalDocumentType.hide()
            this.$refs.tableDocumentTypes.updateRow({
              id: document_type.id,
              role: document_type?.role || {},
              role_id: document_type?.role_id || '',
              document_type_category: document_type.document_type_category,
              document_type_code: document_type.document_type_code,
              document_type_name: document_type.document_type_name,
              upload_limit: document_type.upload_limit,
              active: document_type.active,
              updated_at: document_type.updated_at
            })
          })
        }
      )
    }
  }
}
</script>
